import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase.js";
import { callFunction } from "../lib/callFunction.js";
import { formatDateTime, formatDateOnly } from "../lib/datetime.js";
import { formatMoney } from "../lib/money.js";
import { PLATFORM_TIME_ZONE, todayInZone } from "../lib/zone.js";
import { InvoiceAdjust, PendingAdjustments } from "./InvoiceAdjust.js";

interface ClientRow {
  id: string;
  name: string;
}

interface InvoiceRow {
  id: string;
  client_id: string;
  kind: string;
  number: string;
  period_start: string;
  period_end: string;
  total_cents: number;
  status: string;
  sent_at: string | null;
  pdf_path: string | null;
  delivery_status: "delivered" | "delayed" | "bounced" | "complained" | null;
  clients: { name: string } | null;
  invoice_lines: { adjustment: boolean }[] | null;
}

interface RunRow {
  id: number;
  ran_at: string;
  job: string;
  status: "ok" | "blocked" | "failed";
  detail: Record<string, unknown>;
}

const STATUSES = ["draft", "sent", "failed", "void", "paid"];

/** Messages an admin can act on, keyed by the codes the database and Edge Functions return. */
const ERROR_TEXT: Record<string, string> = {
  usage_sync_not_current: "The usage sync has not finished OK since that month ended. Run a sync on the VoIP usage screen first, or tick the override.",
  billing_settings_incomplete: "Fill in the issuer name, ABN and from address under Developer info first.",
  missing_resend_api_key: "The Resend API key is not set on the Edge Functions.",
  not_authorized: "Only platform admins can do that.",
  period_must_start_on_the_1st: "The period must be a whole month.",
};

/** First day of the month `offset` months from now, as YYYY-MM (what <input type="month"> uses). */
function monthValue(offset: number): string {
  // The current month on the platform's own (Sydney) calendar: billing months are Sydney months.
  const [y = 1970, m = 1] = todayInZone(PLATFORM_TIME_ZONE).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1 + offset, 1)).toISOString().slice(0, 7);
}

async function openPdf(path: string) {
  const { data } = await supabase.storage.from("invoices").createSignedUrl(path, 60);
  if (data?.signedUrl) window.open(data.signedUrl, "_blank", "noopener");
}

/**
 * Invoices are generated on a schedule by fn_run_scheduled_base_invoices / fn_run_scheduled_usage_invoices
 * (supabase/migrations/0025_billing_operations.sql) and emailed by the send-invoice Edge Function. This page is the
 * admin side: generate a period now (same idempotent SQL, so pressing it twice never double-issues), send or resend
 * an invoice with its PDF, open the PDF, correct a status by hand (void, mark paid -- there is no payment webhook),
 * and see the recent scheduled runs, including any that were blocked or failed.
 */
export function Invoices() {
  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [clientFilter, setClientFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adjusting, setAdjusting] = useState<InvoiceRow | null>(null);

  const [genKind, setGenKind] = useState<"base" | "usage">("usage");
  const [genMonth, setGenMonth] = useState(monthValue(-1));
  const [ignoreSync, setIgnoreSync] = useState(false);

  async function load() {
    setLoading(true);
    const [{ data: inv }, { data: cli }, { data: rn }] = await Promise.all([
      supabase
        .from("invoices")
        .select("id, client_id, kind, number, period_start, period_end, total_cents, status, sent_at, pdf_path, delivery_status, clients(name), invoice_lines(adjustment)")
        .order("period_start", { ascending: false })
        .limit(200),
      supabase.from("clients").select("id, name").order("name"),
      supabase.from("billing_runs").select("id, ran_at, job, status, detail").order("ran_at", { ascending: false }).limit(15),
    ]);
    setRows((inv ?? []) as unknown as InvoiceRow[]);
    setClients((cli ?? []) as ClientRow[]);
    setRuns((rn ?? []) as RunRow[]);
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, []);

  async function updateStatus(row: InvoiceRow, status: string) {
    await supabase
      .from("invoices")
      .update({ status, sent_at: status === "sent" ? new Date().toISOString() : row.sent_at })
      .eq("id", row.id);
    await load();
  }

  async function generate() {
    setBusy("generate");
    setNotice(null);
    const { data, error } = await supabase.rpc("rpc_admin_generate_invoices", {
      p_kind: genKind,
      p_period_start: `${genMonth}-01`,
      p_ignore_sync: genKind === "usage" && ignoreSync,
    });
    if (error) setNotice(ERROR_TEXT[error.message] ?? error.message);
    else {
      const list = (data ?? []) as { invoice_id: string | null; skipped_reason: string | null }[];
      const created = list.filter((r) => r.invoice_id).length;
      const skipped = list.length - created;
      setNotice(`Created ${created} draft invoice${created === 1 ? "" : "s"}${skipped ? `; ${skipped} skipped (already invoiced, no usage or no rate card)` : ""}.`);
    }
    setBusy(null);
    await load();
  }

  async function send(row: InvoiceRow) {
    setBusy(row.id);
    setNotice(null);
    const res = await callFunction<{ results?: { ok: boolean; reason?: string }[] }>("send-invoice", { invoice_id: row.id, resend: row.status === "sent" });
    if (!res.ok) setNotice(ERROR_TEXT[res.error] ?? res.error);
    else {
      const r = res.data.results?.[0];
      setNotice(r?.ok ? `${row.number} sent.` : `${row.number} was not sent: ${r?.reason ?? "unknown reason"}`);
    }
    setBusy(null);
    await load();
  }

  const visible = clientFilter ? rows.filter((r) => r.client_id === clientFilter) : rows;

  if (loading && rows.length === 0) return <p className="rc-loading">Loading...</p>;

  return (
    <div>
      <h1 className="rc-h1" style={{ marginBottom: 16 }}>Invoices</h1>

      <fieldset style={{ border: "1px solid var(--rc-border)", borderRadius: 6, padding: 12, marginBottom: 20 }}>
        <legend style={{ fontSize: 13, color: "var(--rc-text-muted)" }}>Generate invoices now</legend>
        <div style={{ display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap", fontSize: 14 }}>
          <label>
            Kind
            <select
              value={genKind}
              onChange={(e) => {
                const k = e.target.value as "base" | "usage";
                setGenKind(k);
                setGenMonth(monthValue(k === "usage" ? -1 : 1));
              }}
              style={{ display: "block", padding: 6, marginTop: 4 }}
            >
              <option value="usage">Usage (minutes, SMS, summaries)</option>
              <option value="base">Base fee</option>
            </select>
          </label>
          <label>
            Month
            <input type="month" value={genMonth} onChange={(e) => setGenMonth(e.target.value)} style={{ display: "block", padding: 6, marginTop: 4 }} />
          </label>
          <button onClick={() => void generate()} disabled={busy !== null || !genMonth} style={{ padding: "7px 14px" }}>
            {busy === "generate" ? "Working..." : "Generate drafts"}
          </button>
        </div>
        {genKind === "usage" && (
          <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, fontSize: 13, color: "var(--rc-text-muted)" }}>
            <input type="checkbox" checked={ignoreSync} onChange={(e) => setIgnoreSync(e.target.checked)} />
            Run even if the usage sync has not finished OK since that month ended (the invoice may be short)
          </label>
        )}
        <p style={{ fontSize: 12, color: "var(--rc-text-faint)", margin: "10px 0 0" }}>
          Drafts are not emailed until you press Send on a row (or the scheduled run sends them, if automatic sending is on in Developer info).
        </p>
      </fieldset>

      <PendingAdjustments clients={clients} />

      {notice && <p style={{ fontSize: 14, background: "var(--rc-surface-sunken)", padding: 10, borderRadius: 6 }}>{notice}</p>}

      <label style={{ display: "block", marginBottom: 16, fontSize: 14 }}>
        Client
        <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} style={{ display: "block", padding: 8, marginTop: 4 }}>
          <option value="">All clients</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>

      <div className="rc-table-scroll">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid var(--rc-border)", color: "var(--rc-text-muted)" }}>
              <th style={{ padding: "10px 12px" }}>Number</th>
              <th style={{ padding: "10px 12px" }}>Client</th>
              <th style={{ padding: "10px 12px" }}>Period</th>
              <th style={{ padding: "10px 12px" }}>Kind</th>
              <th style={{ padding: "10px 12px" }}>Total</th>
              <th style={{ padding: "10px 12px" }}>Status</th>
              <th style={{ padding: "10px 12px" }} />
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.id} style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
                <td style={{ padding: "10px 12px" }}>{r.number}</td>
                <td style={{ padding: "10px 12px" }}>{r.clients?.name ?? "-"}</td>
                <td style={{ padding: "10px 12px" }}>
                  {formatDateOnly(r.period_start)} – {formatDateOnly(r.period_end)}
                </td>
                <td style={{ padding: "10px 12px" }}>{r.kind}</td>
                <td style={{ padding: "10px 12px" }}>
                  {formatMoney(r.total_cents)}
                  {r.invoice_lines?.some((l) => l.adjustment) && <span style={{ marginLeft: 6, fontSize: 12, color: "var(--rc-text-faint)" }}>adjusted</span>}
                </td>
                <td style={{ padding: "10px 12px" }}>
                  <select value={r.status} onChange={(e) => void updateStatus(r, e.target.value)} style={{ padding: 4 }}>
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  {r.delivery_status && (
                    <span style={{ marginLeft: 8, fontSize: 12, color: r.delivery_status === "bounced" || r.delivery_status === "complained" ? "var(--rc-danger)" : "var(--rc-text-faint)" }}>
                      {r.delivery_status === "complained" ? "marked as spam" : r.delivery_status}
                    </span>
                  )}
                </td>
                <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                  {r.kind === "base" && (r.status === "draft" || r.status === "failed") && (
                    <button onClick={() => setAdjusting(r)} disabled={busy !== null} style={{ padding: "3px 10px", marginRight: 8 }}>
                      Adjust
                    </button>
                  )}
                  {(r.status === "draft" || r.status === "failed" || r.status === "sent") && (
                    <button
                      onClick={() => {
                        if (r.status !== "sent" || window.confirm(`Email ${r.number} to the client again?`)) void send(r);
                      }}
                      disabled={busy !== null}
                      style={{ padding: "3px 10px", marginRight: 8 }}
                    >
                      {busy === r.id ? "Sending..." : r.status === "draft" ? "Send" : r.status === "failed" ? "Retry" : "Resend"}
                    </button>
                  )}
                  {r.pdf_path && (
                    <button onClick={() => void openPdf(r.pdf_path!)} style={{ background: "none", border: "none", color: "var(--rc-link)", cursor: "pointer", padding: 0 }}>
                      PDF
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 16, color: "var(--rc-text-faint)" }}>
                  No invoices yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h2 className="rc-h2" style={{ margin: "28px 0 12px" }}>Recent scheduled and manual runs</h2>
      <div className="rc-table-scroll">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} style={{ borderBottom: "1px solid var(--rc-border-soft)", verticalAlign: "top" }}>
                <td style={{ padding: 6, whiteSpace: "nowrap" }}>{formatDateTime(r.ran_at)}</td>
                <td style={{ padding: 6 }}>{r.job}</td>
                <td style={{ padding: 6, color: r.status === "ok" ? "var(--rc-success)" : r.status === "blocked" ? "var(--rc-warning)" : "var(--rc-danger)" }}>{r.status}</td>
                <td style={{ padding: 6, color: "var(--rc-text-muted)" }}>{String(r.detail.reason ?? "") || JSON.stringify(r.detail)}</td>
              </tr>
            ))}
            {runs.length === 0 && (
              <tr>
                <td style={{ padding: 12, color: "var(--rc-text-faint)" }}>No runs yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {adjusting && (
        <InvoiceAdjust
          invoice={{ id: adjusting.id, number: adjusting.number, period_start: adjusting.period_start, period_end: adjusting.period_end, clientName: adjusting.clients?.name ?? "-" }}
          onClose={() => setAdjusting(null)}
          onChanged={() => void load()}
        />
      )}
    </div>
  );
}
